import { thunkAddSymbol } from '@store/migrationActions/thunkActions/thunkSymbolActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { PROJECT_SETTINGS_PERMISSION } from '@/permissions/project/definitions'
import { Empty } from './components/Empty'
import { LibrarySymbols } from './components/LibrarySymbols'
import { Scope, type SymbolScope } from './components/Scope'
import { Symbols } from './components/Symbols'

const SymbolLibrary: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const [scope, setScope] = useState<SymbolScope>('project')

  const symbols = useSelector(migrationProjectSelector.getProjectSymbols)
  const team = useSelector(migrationProjectSelector.getProjectTeam)

  const { hasAccess, hasNotAvailableAccessBadge } = useProjectAccess(
    PROJECT_SETTINGS_PERMISSION.WORKSPACE_LIBRARY_TAB
  )

  const artboardDisabled = useArtBoardDisabled()

  // Those with no library of their own here (guests, members of someone
  // else's project) get the project symbols only, without the switch.
  const showScope = hasAccess || hasNotAvailableAccessBadge
  const libraryTitle = team ? team.name : 'My library'

  const onCreateFirst = () => {
    sendGtmEvent(
      GtmTriggerName.SYMBOL_ADD_SIDEBAR,
      'Click to create symbol in sidebar (first symbol)'
    )

    dispatch(thunkAddSymbol())
  }

  return (
    <div>
      {showScope && (
        <Scope
          scope={scope}
          libraryTitle={libraryTitle}
          locked={hasNotAvailableAccessBadge}
          onChange={setScope}
        />
      )}

      {scope === 'project' && symbols.length === 0 && (
        <Empty disabled={artboardDisabled} onCreate={onCreateFirst} />
      )}
      {scope === 'project' && symbols.length > 0 && <Symbols />}
      {scope === 'library' && <LibrarySymbols />}
    </div>
  )
}

export default SymbolLibrary
